import { Divider, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { styled } from '@mui/material/styles';
import {
  DrawerBody,
  Heading,
  DrawerInputBox,
  DrawerInput,
  SearchIcon,
  DrawerQuestion,
  DrawerQuestions,
  DrawerSubject,
  DrawerSubjects,
  DrawerVericalNav,
  DrawerVericalNavData,
  DividerContainer,
  SubjectTitle,
  RecentBtn,
  StyledAccessTimeIcon,
  StyledMoreVertIcon,
  LoadingDiv,
  SidebarContainer,
  SidebarContent,
  DrawerHeader,
  QuestionTitle,
  DraftRowHeader,
  DraftFileIcon,
  DraftMenuButton,
  DraftRenameInput,
  SidebarHeader,
  SidebarIconButton,
  CollapsedRail,
  CollapsedRailButton,
  SubjectFilterContainer,
} from './style';
import DropdownMenu from '../DropdownMenu';
import plusWhite from '../../static/img/plusWhite.svg';
import Loader from '../Loader';
import SmallLoader from '../Loader/SmallLoader';
import DeleteGetFeedbackPopup from '../DeleteGetFeedbackPopUp';
import {
  createDraftWithSettings,
  deleteAssignment,
  updateAssignmentDisplayTitle,
} from '../../service';
import PopupDialogueBox from '../../components2/PopupDialogueBox';
import { setStoredSubjectFilter } from '../../utils/myDraftsPrefs';
import {
  draftDisplayTitle,
  draftQuestionTitle,
  draftRenamedTitle,
  draftRoutePath,
  draftSubjectOf,
  isMultiResponseDraft,
} from '../../utils/draftRows';
import purpleFileIcon from '../../static/icons/purple-file-icon.svg';
import blackFileIcon from '../../static/icons/black-file-icon.svg';
import { NewDraftSplitButton } from '../BulkUpload/components/NewDraftSplitButton';
import { useBulkUploadGate } from '../BulkUpload/components/BulkUploadPlanGate';
import {
  canAccessAnyBulkUpload,
  isStudentMyDraftsUser,
} from '../BulkUpload/rules';
import { useProfile } from '../state/hooks';

const menuPaperSx = {
  minWidth: 140,
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  '& .MuiMenuItem-root': {
    fontFamily: 'var(--font-family-ibm_plex_sans)',
    fontSize: '14px',
    lineHeight: '18px',
    padding: '8px 16px',
  },
};

/**
 * One draft row: its name on a single line (a long one ellipsises, the full name
 * is the tooltip) with an actions button on the right that opens Rename and
 * Delete. While renaming, the line becomes an input: Enter or leaving the field
 * saves, Escape discards. Extracted because the virtualized and non-virtualized
 * lists render exactly the same body.
 */
const DraftRow = ({
  draft,
  isCurrent,
  isRenaming,
  onOpen,
  onRename,
  onRenameSubmit,
  onRenameCancel,
  onDelete,
  ...rest
}) => {
  const isBulk = isMultiResponseDraft(draft);
  const title = draftDisplayTitle(draft);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);
  // An action runs once the menu has finished closing, not on the click. The
  // menu restores focus to where it was when it closes, which would blur a
  // rename input that had just mounted and end the rename with nothing typed;
  // so focus is put back by hand only when the menu was dismissed.
  const menuButtonRef = useRef(null);
  const pendingActionRef = useRef(null);
  const chooseAction = (action) => {
    pendingActionRef.current = action;
    closeMenu();
  };
  const handleMenuExited = () => {
    const run = pendingActionRef.current;
    pendingActionRef.current = null;
    if (run) run();
    else menuButtonRef.current?.focus();
  };
  // Enter saves and unmounts the input, and the browser can still blur it on
  // the way out; one rename session must reach the parent exactly once.
  const renameSettled = useRef(false);
  useEffect(() => {
    renameSettled.current = false;
  }, [isRenaming]);
  const settleRename = (fn) => {
    if (renameSettled.current) return;
    renameSettled.current = true;
    fn();
  };
  return (
    <DrawerQuestion
      studentStyle={isCurrent}
      // Selection was carried by background colour alone, which neither a
      // screen reader nor a test can read.
      aria-current={isCurrent ? 'true' : undefined}
      onClick={isRenaming ? undefined : onOpen}
      {...rest}
    >
      <DraftRowHeader>
        {isBulk && (
          <DraftFileIcon
            src={isCurrent ? purpleFileIcon : blackFileIcon}
            alt=""
            aria-hidden="true"
          />
        )}
        {isRenaming ? (
          <DraftRenameInput
            autoFocus
            type="text"
            aria-label="Draft name"
            defaultValue={title}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const { value } = e.currentTarget;
                settleRename(() => onRenameSubmit(value));
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                settleRename(onRenameCancel);
              }
            }}
            onBlur={(e) => {
              const { value } = e.currentTarget;
              settleRename(() => onRenameSubmit(value));
            }}
          />
        ) : (
          <Tooltip title={title} placement="right" enterDelay={400}>
            <QuestionTitle>{title}</QuestionTitle>
          </Tooltip>
        )}
        {!isRenaming && (
          <DraftMenuButton
            ref={menuButtonRef}
            type="button"
            aria-label={`Actions for ${title}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <MoreVertIcon fontSize="inherit" />
          </DraftMenuButton>
        )}
      </DraftRowHeader>
      {/* The menu is portaled, but React still bubbles its clicks to this row;
          stop them here so choosing an action (or dismissing the menu) does not
          also open the draft. */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        disableRestoreFocus
        TransitionProps={{ onExited: handleMenuExited }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: menuPaperSx }}
        MenuListProps={{ 'aria-label': `Actions for ${title}` }}
      >
        <MenuItem onClick={() => chooseAction(onRename)}>Rename</MenuItem>
        <MenuItem
          onClick={() => chooseAction(onDelete)}
          sx={{ color: '#a93421' }}
        >
          Delete
        </MenuItem>
      </Menu>
    </DrawerQuestion>
  );
};

const drawerWidth = 219;
const collapsedWidth = 66;

const SidebarOpenGlyph = () => (
  <img src="img/draft-sidebar-open.svg" alt="" aria-hidden="true" />
);

const SidebarCollapsedGlyph = () => (
  <img src="img/draft-sidebar-collapsed.svg" alt="" aria-hidden="true" />
);

const PlusGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 3V13"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 8H13"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function IndepentdentUserSidebar({
  open,
  subjects,
  setSelectedSubject,
  selectedSubject,
  allSubjectOptions,
  groupedAndSortedData,
  currentDraftRowId,
  deleteQuestionFunction,
  onDraftDeleted,
  onToggleSidebar,
  onDraftCreated,
  onBulkUploadClick,
  bulkUploadActive = false,
  sidebarOffset = 80,
}) {
  const [pageHeight, setPageHeight] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  // Every teacher sees the Bulk Upload option; on the free tier selecting it
  // opens the upgrade prompt instead of the wizard (see useBulkUploadGate).
  const bulkUploadGate = useBulkUploadGate();
  const showBulkUploadOption =
    canAccessAnyBulkUpload(profile) || bulkUploadGate.locked;
  const isStudent = isStudentMyDraftsUser(profile);

  /**
   * Navigate to a draft document via React Router instead of doing a
   * full window.location.reload(). The reload was added defensively in
   * commit 30d8d463 ("added location reload to refresh page") because
   * the destination route appeared to keep stale data when navigated to
   * in-memory — the destination component (FeedbacksRoot) keys its
   * queries on the `id` URL param via useSubmissionById(id) +
   * useCommentsById(... Boolean(id)) + useOverAllCommentsById(...), but
   * createGlobalState is configured with refetchOnMount: false, so a
   * cached entry from a prior session-visit of the same doc would
   * render stale data on remount without a fresh fetch.
   *
   * To match the old reload's "always fresh" guarantee without the
   * full SPA tear-down, we removeQueries() (not invalidateQueries —
   * invalidate only refetches ACTIVE queries; the destination's queries
   * are still inactive at the moment of navigation, and would then
   * mount cached-stale because of refetchOnMount: false). removeQueries
   * wipes the cache entry entirely; the next mount sees no data and
   * fetches fresh. For a newly-created draft this is a no-op (no prior
   * entry). For sidebar nav between existing docs in the same session,
   * this gives the destination a clean slate. Caught by codex review.
   *
   * Net effect: Create Draft drops from ~25-45s (full SPA reload + cold
   * cache cascade) to whatever it takes the destination route to fetch
   * the new doc's data alone — typically 3-8s, dominated by the
   * /students/* and /teachers/classes/* preferences endpoints (which
   * are slow for separate reasons and not addressed here).
   */
  const goToDraft = useCallback(
    (draft) => {
      const path = draftRoutePath(draft);
      if (!path) return;
      const docId = draft.submissionId ?? draft.id;
      queryClient.removeQueries({ queryKey: ['submissionById', docId] });
      queryClient.removeQueries({
        queryKey: ['commentsById'],
        predicate: (q) => {
          const params = q.queryKey?.[1];
          if (!params || typeof params !== 'object') return false;
          return params.submissionId === docId || params.id === docId;
        },
      });
      queryClient.removeQueries({
        queryKey: ['overAllCommentsById'],
        predicate: (q) => {
          const params = q.queryKey?.[1];
          if (!params || typeof params !== 'object') return false;
          return params.submissionId === docId || params.id === docId;
        },
      });
      history.push(path);
    },
    [history, queryClient]
  );

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreateDraftPopup, setShowCreateDraftPopup] = useState(false);
  const questionsScrollRef = useRef(null);

  const subjectsList = React.useMemo(
    () =>
      Array.isArray(subjects)
        ? subjects
        : subjects && typeof subjects === 'object'
        ? Object.values(subjects).flat()
        : [],
    [subjects]
  );

  const subjectOptionsFromItems = React.useMemo(() => {
    if (Array.isArray(allSubjectOptions) && allSubjectOptions.length > 0) {
      const set = new Set(allSubjectOptions);
      if (selectedSubject) set.add(selectedSubject);
      return Array.from(set).sort();
    }
    const set = new Set();
    if (Array.isArray(subjects)) {
      subjects.forEach((item) => {
        const sub = draftSubjectOf(item);
        if (sub) set.add(sub);
      });
    } else if (
      subjects &&
      typeof subjects === 'object' &&
      !Array.isArray(subjects)
    ) {
      Object.keys(subjects)
        .filter(Boolean)
        .forEach((k) => set.add(k));
    }
    if (selectedSubject) set.add(selectedSubject);
    return Array.from(set).sort();
  }, [allSubjectOptions, subjects, selectedSubject]);

  // Filtered list computed directly - no useEffect needed
  const filteredQuestions = React.useMemo(() => {
    return subjectsList.filter((question) => {
      if (!question?.id) return false;
      if (question.id === currentDraftRowId) return true;
      // A renamed draft is still findable by the question it was renamed from.
      const needle = debouncedSearch.toLowerCase();
      const matchesSearch = [
        draftDisplayTitle(question),
        draftQuestionTitle(question),
      ].some((name) => name.toLowerCase().includes(needle));
      if (!selectedSubject) return matchesSearch;
      const questionSubject = draftSubjectOf(question);
      return matchesSearch && questionSubject === selectedSubject;
    });
  }, [subjectsList, debouncedSearch, selectedSubject, currentDraftRowId]);

  const VIRTUALIZE_THRESHOLD = 50;
  const shouldVirtualize = filteredQuestions.length > VIRTUALIZE_THRESHOLD;
  // Keep measured heights with their drafts when sorting or filtering moves rows.
  const getItemKey = useCallback(
    (index) => filteredQuestions[index].id,
    [filteredQuestions]
  );

  const virtualizer = useVirtualizer({
    count: filteredQuestions.length,
    getScrollElement: () => questionsScrollRef.current,
    getItemKey,
    estimateSize: () => 44,
    gap: 10,
    enabled: shouldVirtualize,
  });

  useEffect(() => {
    if (!currentDraftRowId || !filteredQuestions.length) return;
    const index = filteredQuestions.findIndex(
      (q) => q.id === currentDraftRowId
    );
    if (index < 0) return;

    if (shouldVirtualize) {
      virtualizer.scrollToIndex(index, { align: 'center' });
      return;
    }

    const container = questionsScrollRef.current;
    if (!container) return;
    const selectedEl = container.querySelector(
      `[data-doc-id="${currentDraftRowId}"]`
    );
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [currentDraftRowId, filteredQuestions, shouldVirtualize]);

  // Handle subject filter change - navigate if current doc doesn't match
  const handleSubjectFilterChange = React.useCallback(
    (newSubject) => {
      setSelectedSubject(newSubject);
      // Persist the explicit choice (including "All Subjects" => null) so it is
      // retained when navigating away from and back to My Drafts. Gate to the
      // drafts route so a selection made if this sidebar is ever reused
      // elsewhere can't leak into the My Drafts preference.
      if (window.location.hash.includes('/documents/')) {
        setStoredSubjectFilter(newSubject ?? null);
      }

      if (!newSubject || !currentDraftRowId || !subjectsList.length) return;

      const currentDoc = subjectsList.find((q) => q.id === currentDraftRowId);
      if (!currentDoc) return;

      const currentDocSubject = draftSubjectOf(currentDoc);
      if (currentDocSubject === newSubject) return;

      // Current doc doesn't match filter - navigate to first matching doc
      const firstMatchingDoc = subjectsList.find(
        (q) => draftSubjectOf(q) === newSubject
      );
      if (firstMatchingDoc && firstMatchingDoc.id !== currentDraftRowId) {
        goToDraft(firstMatchingDoc);
      }
    },
    [setSelectedSubject, currentDraftRowId, subjectsList, goToDraft]
  );

  useEffect(() => {
    const updateHeight = () => {
      const fullHeight = document.documentElement.scrollHeight;
      if (fullHeight !== pageHeight) {
        setPageHeight(fullHeight - 170);
      }
    };

    const observer = new MutationObserver((mutations) => {
      let shouldUpdateHeight = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldUpdateHeight = true;
          break;
        }
      }

      if (shouldUpdateHeight) {
        updateHeight();
      }
    });

    const config = { childList: true, subtree: true };

    observer.observe(document.body, config);

    updateHeight();

    return () => observer.disconnect();
  }, []);

  // An assignment-backed draft has no submission to delete — the row IS the
  // assignment, so discarding it is deleteAssignment. Everything else keeps the
  // submission-scoped path the parent owns. Either way the parent is told which
  // row went, because only the parent knows where the reader should land next.
  const confirmDelete = async (draft) => {
    if (!isMultiResponseDraft(draft)) {
      deleteQuestionFunction(draft.id);
      return;
    }
    await deleteAssignment(draft.assignmentId).catch(() => {});
    await queryClient.refetchQueries({ queryKey: ['allDocuments'] });
    onDraftDeleted?.(draft.assignmentId);
  };

  const [renamingRowId, setRenamingRowId] = useState(null);

  // Every list this sidebar is fed from reads the drafts cache (and the open
  // document reads its submission), so a rename is written into each cache that
  // carries the assignment: the row re-renders at once, and the server's answer
  // then replaces the optimistic value or, on failure, the old one comes back.
  const applyDisplayTitle = useCallback(
    (assignmentId, displayTitle) => {
      queryClient.setQueryData(['allDocuments'], (rows) =>
        Array.isArray(rows)
          ? rows.map((row) =>
              (row?.assignmentId ?? row?.assignment?.id) === assignmentId
                ? { ...row, displayTitle }
                : row
            )
          : rows
      );
      queryClient
        .getQueriesData({ queryKey: ['submissionById'] })
        .forEach(([key, data]) => {
          if (data?.assignment?.id === assignmentId) {
            queryClient.setQueryData(key, {
              ...data,
              assignment: { ...data.assignment, displayTitle },
            });
          }
        });
      // Two keys hold an assignment: the document page's and the bulk-draft
      // wizard's (DraftWizardPage keys its read under 'assignment').
      ['assignmentById', 'assignment'].forEach((cacheKey) => {
        queryClient.setQueryData([cacheKey, assignmentId], (data) =>
          data?.id === assignmentId ? { ...data, displayTitle } : data
        );
      });
    },
    [queryClient]
  );

  const renameDraft = async (draft, value) => {
    setRenamingRowId(null);
    const assignmentId = draft?.assignmentId;
    if (!assignmentId) return;
    // A blank name clears the rename: the row goes back to its question.
    const next = (value ?? '').trim() || null;
    const previous = draftRenamedTitle(draft);
    // Leaving the input as it was found is a cancel, not a rename: on a row that
    // still goes by its question, the input opened holding that question, and
    // pinning it as a name would freeze the row while the question moves on.
    if (next === previous || next === draftDisplayTitle(draft)) return;
    applyDisplayTitle(assignmentId, next);
    try {
      const saved = await updateAssignmentDisplayTitle(assignmentId, next);
      if (saved?.id === assignmentId && 'displayTitle' in saved) {
        applyDisplayTitle(assignmentId, saved.displayTitle ?? null);
      }
    } catch (_) {
      // The API layer has already shown the error toast.
      applyDisplayTitle(assignmentId, previous);
    }
  };

  const deleteFunction = () => {
    setShowDeletePopup(false);
    if (deleteTarget) confirmDelete(deleteTarget);
  };

  const openCreateDraftPopup = () => {
    setShowCreateDraftPopup(true);
  };

  const openBulkUploadFlow = bulkUploadGate.guard(() => {
    onBulkUploadClick?.();
  });

  const handleCreateDraft = (settings) => {
    return createDraftWithSettings({
      subjectName: settings.subjectName,
      subjectCode: settings.subjectCode,
      taskType: settings.taskType,
      state: settings.state,
      year: settings.year,
      responseType: settings.responseType,
      totalMarks: settings.totalMarks,
      title: 'Untitled Question',
    })
      .then(async (response) => {
        if (response?.id) {
          await Promise.resolve(onDraftCreated?.());
          goToDraft({ submissionId: response.id, submissionCount: 1 });
        }
      })
      .catch(() => {});
  };

  return (
    <>
      {bulkUploadGate.popup}
      {showDeletePopup && (
        <DeleteGetFeedbackPopup
          showDeletePopup={showDeletePopup}
          hidedeletePopup={() => setShowDeletePopup(false)}
          deleteFunction={deleteFunction}
        />
      )}
      {showCreateDraftPopup && (
        <PopupDialogueBox
          open={showCreateDraftPopup}
          close={() => setShowCreateDraftPopup(false)}
          mode="create"
          onCreateDraft={handleCreateDraft}
        />
      )}
      <SidebarContainer
        drawerWidth={drawerWidth}
        collapsedWidth={collapsedWidth}
        open={open}
        offset={sidebarOffset}
        elevated={bulkUploadActive}
      >
        <CollapsedRail collapsedWidth={collapsedWidth} open={open}>
          <Tooltip title="Show drafts" placement="right">
            <CollapsedRailButton
              aria-label="Show drafts sidebar"
              onClick={onToggleSidebar}
              type="button"
            >
              <SidebarCollapsedGlyph />
            </CollapsedRailButton>
          </Tooltip>
          <Tooltip title="New draft" placement="right">
            <CollapsedRailButton
              aria-label="Create new draft"
              onClick={openCreateDraftPopup}
              variant="circle"
              type="button"
            >
              <PlusGlyph />
            </CollapsedRailButton>
          </Tooltip>
        </CollapsedRail>
        {open && (
          <SidebarContent>
            <SidebarHeader>
              <Tooltip title="Hide sidebar" placement="right">
                <SidebarIconButton
                  aria-label="Hide drafts sidebar"
                  onClick={onToggleSidebar}
                  type="button"
                >
                  <SidebarOpenGlyph />
                </SidebarIconButton>
              </Tooltip>
            </SidebarHeader>
            <div style={{ margin: 8 }}>
              {showBulkUploadOption ? (
                <NewDraftSplitButton
                  onSingleDraftClick={openCreateDraftPopup}
                  onBulkUploadClick={openBulkUploadFlow}
                />
              ) : (
                <DrawerHeader
                  onClick={openCreateDraftPopup}
                  style={{
                    margin: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '8px 14px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    {isStudent && (
                      <img src={plusWhite} alt="" width={16} height={16} aria-hidden="true" />
                    )}
                    {isStudent ? 'New Draft' : '+ New Draft'}
                  </span>
                </DrawerHeader>
              )}
            </div>
            <DividerContainer>
              <Divider />
            </DividerContainer>
            <DrawerBody>
              {!subjects ? (
                <LoadingDiv>
                  <SmallLoader />
                </LoadingDiv>
              ) : (
                <>
                  <DrawerInputBox>
                    <DrawerInput
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <SearchIcon src="img/search14gray.svg" />
                  </DrawerInputBox>
                  <SubjectFilterContainer>
                    <DropdownMenu
                      key={`subject-filter-${subjectOptionsFromItems.length}-${
                        selectedSubject || 'all'
                      }`}
                      primaryText={selectedSubject || 'All Subjects'}
                      menuItems={[
                        { id: 'all', title: 'All Subjects' },
                        ...subjectOptionsFromItems.map((sub) => ({
                          id: String(sub),
                          title: String(sub),
                        })),
                      ]}
                      selectedIndex={Math.max(
                        0,
                        Math.min(
                          selectedSubject
                            ? subjectOptionsFromItems.findIndex(
                                (s) => String(s) === String(selectedSubject)
                              ) + 1
                            : 0,
                          subjectOptionsFromItems.length
                        )
                      )}
                      onItemSelected={(item) =>
                        handleSubjectFilterChange(
                          item?.id === 'all' ? null : item?.id
                        )
                      }
                    />
                  </SubjectFilterContainer>
                  {shouldVirtualize ? (
                    <DrawerQuestions ref={questionsScrollRef}>
                      <div
                        style={{
                          height: `${virtualizer.getTotalSize()}px`,
                          flexShrink: 0,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {virtualizer.getVirtualItems().map((virtualRow) => {
                          const question = filteredQuestions[virtualRow.index];
                          return (
                            <div
                              key={virtualRow.key}
                              data-index={virtualRow.index}
                              ref={virtualizer.measureElement}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`,
                              }}
                            >
                              <DraftRow
                                draft={question}
                                isCurrent={question.id === currentDraftRowId}
                                isRenaming={question.id === renamingRowId}
                                onOpen={() => goToDraft(question)}
                                onRename={() => setRenamingRowId(question.id)}
                                onRenameSubmit={(value) =>
                                  renameDraft(question, value)
                                }
                                onRenameCancel={() => setRenamingRowId(null)}
                                onDelete={() => {
                                  setDeleteTarget(question);
                                  setShowDeletePopup(true);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </DrawerQuestions>
                  ) : (
                    <DrawerQuestions ref={questionsScrollRef}>
                      {filteredQuestions.map((question, qIndex) => (
                        <DraftRow
                          key={question.id ?? qIndex}
                          data-doc-id={question.id}
                          draft={question}
                          isCurrent={question.id === currentDraftRowId}
                          isRenaming={question.id === renamingRowId}
                          onOpen={() => goToDraft(question)}
                          onRename={() => setRenamingRowId(question.id)}
                          onRenameSubmit={(value) =>
                            renameDraft(question, value)
                          }
                          onRenameCancel={() => setRenamingRowId(null)}
                          onDelete={() => {
                            setDeleteTarget(question);
                            setShowDeletePopup(true);
                          }}
                        />
                      ))}
                    </DrawerQuestions>
                  )}
                </>
              )}
            </DrawerBody>
          </SidebarContent>
        )}
      </SidebarContainer>
    </>
  );
}

export default IndepentdentUserSidebar;
